/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { toast } from './toast'
import * as tasksApi from './api/tasks'
import type { Task, ESignature } from './api/types'

// Extended interfaces for task service functionality
export interface TaskFlags {
  rejectESignatureRequired: boolean
  approveESignatureRequired: boolean
  revertESignatureRequired: boolean
  canRevert: boolean
  canUploadFromOneDrive: boolean
}

export interface TaskFileFsi {
  id: number
  name: string
  type: string
  versionId: number
  minorVersionId: number
  isConvertable: boolean
  isBlindedDocument: boolean
}

export interface TaskFile {
  sourceFsi?: TaskFileFsi
  draftFsi?: TaskFileFsi
  targetFsi?: TaskFileFsi
}

export interface ExtendedTask extends Task {
  workflowUuid?: string // renamed from workflowId to avoid conflict with Task
  workflowStepType: string
  flags: TaskFlags
  files: TaskFile[]
  canCancelWorkflow?: boolean
  taskType: string
}

export interface RejectReason {
  id: number
  text: string
}

export interface RejectRequest {
  comment: string
  rejectReasonId?: RejectReason | null
  esignature?: ESignature
}

export interface TaskServiceParams {
  isSimplified?: boolean
  action?: number
  successMsg?: string
  esignature?: ESignature
}

export interface ApproveTaskResponse {
  uploadedFromExternalEditor?: boolean
}

export interface OneDriveAccessResponse {
  valid: boolean
  error?: string
}

/**
 * Signature for the signAction function from useEsignature hook.
 * Callers (React components) obtain this via useEsignature() and pass it
 * into TaskService methods so the static service can trigger the e-signature
 * UI flow without depending on React hooks directly.
 */
export type SignActionFn = (
  actionType: string,
  actionName: string,
  options?: { esignMsg?: string }
) => Promise<{ username?: string; password?: string; relayState?: string; result?: unknown }>

/**
 * Normalize a ValidationResult from useEsignature into the ESignature shape
 * expected by the tasks API.
 *  - SSO / OKTA  → { relayState }
 *  - DSO (RCM)   → { username, password }
 */
function normalizeValidationToESignature(validation: {
  username?: string
  password?: string
  relayState?: string
}): ESignature {
  if (validation.relayState) {
    return { relayState: validation.relayState } as unknown as ESignature
  }
  return {
    username: validation.username || '',
    password: validation.password || ''
  } as unknown as ESignature
}

// Workflow step types constants (matching AngularJS constants)
export const WorkflowStepType = {
  APPROVE: 'APPROVE',
  APPROVE_MULTIFILE: 'APPROVE_MULTIFILE',
  APPROVE_MIGRATION: 'APPROVE_MIGRATION',
  APPROVE_EOS: 'APPROVE_EOS',
  REVIEW: 'REVIEW',
  REVIEW_MULTIFILE: 'REVIEW_MULTIFILE',
  REVIEW_EOS: 'REVIEW_EOS',
  REVISE_EDIT: 'REVISE_EDIT',
  REVISE_EDIT_MULTIFILE: 'REVISE_EDIT_MULTIFILE',
  REVISE_EDIT_MIGRATION: 'REVISE_EDIT_MIGRATION',
  REVISE_EDIT_EOS: 'REVISE_EDIT_EOS',
  APPROVE_FILE_ARTIFACT: 'APPROVE_FILE_ARTIFACT',
  REVISE_EDIT_FILE_ARTIFACT: 'REVISE_EDIT_FILE_ARTIFACT',
  REVIEW_FILE_ARTIFACT: 'REVIEW_FILE_ARTIFACT',
  REVISE_EDIT_RECLASSIFICATION: 'REVISE_EDIT_RECLASSIFICATION',
  REVIEW_RECLASSIFICATION: 'REVIEW_RECLASSIFICATION',
  APPROVE_RECLASSIFICATION: 'APPROVE_RECLASSIFICATION',
  TRAINING: 'TRAINING'
} as const

// Task types constants
export const TaskType = {
  USER_REJECTED_TASK: 'USER_REJECTED_TASK',
  USER: 'USER'
} as const

// Task status constants
export const TaskStatus = {
  INACTIVE: 'INACTIVE',
  OPEN: 'OPEN',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  CANCELLED: 'CANCELLED',
  SUSPENDED: 'SUSPENDED'
} as const

/**
 * Task Service - React TypeScript equivalent of AngularJS task.service.js
 *
 * Provides task approval and rejection functionality with e-signature support,
 * toast notifications, and workflow integration.
 */
export class TaskService {
  /**
   * Rejects a task with optional e-signature requirement
   * @param taskId - The ID of the task to reject
   * @param task - The task object containing flags and workflow information
   * @param request - The rejection request with comment and reason
   * @param onApprove - Optional callback to execute after successful rejection
   */
  static async rejectTask(
    taskId: string,
    task: ExtendedTask,
    request: RejectRequest,
    onApprove?: () => void,
    signAction?: SignActionFn
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const callApi = async (esignature?: ESignature) => {
        try {
          let rejectionReason: string | undefined
          let comment = request.comment

          if (request.rejectReasonId) {
            if (request.rejectReasonId.id === 0) {
              rejectionReason = undefined
              comment = request.comment
            } else {
              rejectionReason = request.rejectReasonId.text
            }
          }

          await tasksApi.rejectTask(taskId, comment, rejectionReason, request.rejectReasonId?.id, esignature)

          if (onApprove && typeof onApprove === 'function') {
            onApprove()
          }

          // Emit events for table updates (would need event system in React app)
          // For now, we'll use a simple approach with custom events
          window.dispatchEvent(new CustomEvent('updateTasks', { detail: true }))
          window.dispatchEvent(new CustomEvent('updateDashboard', { detail: true }))
          window.dispatchEvent(new CustomEvent('updateSearchFiles'))

          toast.success('Task rejected successfully')

          // Handle workflow-related tasks
          if (
            task.workflowUuid &&
            [
              WorkflowStepType.APPROVE,
              WorkflowStepType.APPROVE_MULTIFILE,
              WorkflowStepType.APPROVE_MIGRATION,
              WorkflowStepType.APPROVE_EOS,
              WorkflowStepType.REVIEW,
              WorkflowStepType.REVIEW_MULTIFILE,
              WorkflowStepType.REVIEW_EOS,
              WorkflowStepType.REVISE_EDIT,
              WorkflowStepType.REVISE_EDIT_MULTIFILE,
              WorkflowStepType.REVISE_EDIT_MIGRATION,
              WorkflowStepType.REVISE_EDIT_EOS
            ].some((type) => type === task.workflowStepType)
          ) {
            try {
              const data = await tasksApi.getMyTasks()
              const tasksList = data.elements
              const targetTask = tasksList.find((t) => t.taskType === TaskType.USER_REJECTED_TASK)

              if (targetTask) {
                // In a real React app, this would open a modal component
                // For now, we'll just resolve the promise
                setTimeout(() => {
                  resolve()
                }, 100)
              } else {
                resolve()
              }
            } catch {
              resolve()
            }
          } else {
            resolve()
          }
        } catch (error: unknown) {
          const err = error as Record<string, unknown>
          const errorMessage = ((err?.response as Record<string, unknown>)?.data as { message?: string } | undefined)
            ? ((err?.response as Record<string, unknown>)?.data as { message: string }).message
            : (err?.message as string) || 'Unknown error'
          toast.error(`Failed to reject task: ${errorMessage}`)
          reject(error)
        }
      }

      // Check if e-signature is required for rejection
      if (task.flags.rejectESignatureRequired) {
        if (signAction) {
          signAction('ADMIN_ACTION', 'ADMIN_ACTION_TASK_REJECT')
            .then((validation) => {
              const esignature = normalizeValidationToESignature(validation)
              callApi(esignature)
            })
            .catch(() => {
              toast.error('E-signature cancelled')
              reject(new Error('E-signature cancelled by user'))
            })
        } else if (request.esignature) {
          callApi(request.esignature)
        } else {
          toast.error('E-signature is required but no authentication method was provided')
          reject(new Error('E-signature required but signAction not provided'))
        }
      } else {
        // No e-signature required
        callApi()
      }
    })
  }

  /**
   * Approves a task with optional e-signature requirement
   * @param taskId - The ID of the task to approve
   * @param msg - Message for e-signature modal (if required)
   * @param comment - Optional comment for the approval
   * @param task - The task object containing flags and workflow information
   * @param settingParams - Additional parameters for task processing
   * @param onApprove - Optional callback to execute after successful approval
   */
  static async approveTask(
    taskId: string,
    msg: string,
    comment: string,
    task: ExtendedTask,
    settingParams: TaskServiceParams = {},
    onApprove?: () => void,
    signAction?: SignActionFn
  ): Promise<void> {
    const params = { ...task, ...settingParams }
    let docName = ''
    let isRevert = false
    let finalMsg = msg

    return new Promise((resolve, reject) => {
      if (params.isSimplified && params.action === 0) {
        reject(new Error('Invalid action for simplified task'))
        return
      }

      // Determine document name and message based on workflow step type
      if (
        (
          [WorkflowStepType.APPROVE_MIGRATION, WorkflowStepType.APPROVE_MULTIFILE, WorkflowStepType.APPROVE] as string[]
        ).includes(params.workflowStepType)
      ) {
        if (params.files.length > 1) {
          docName = 'Multiple Documents'
        } else if (params.files.length > 0) {
          const file = params.files[0]
          if (file.sourceFsi) {
            docName = file.sourceFsi.name
          } else if (file.draftFsi) {
            docName = file.draftFsi.name
          } else if (file.targetFsi) {
            docName = file.targetFsi.name
          }
        }

        finalMsg = `Approval for document: ${docName}`
      } else if (params.workflowStepType === WorkflowStepType.TRAINING) {
        if (params.files.length === 1) {
          const file = params.files[0]
          if (file.sourceFsi) {
            docName = file.sourceFsi.name
          } else if (file.draftFsi) {
            docName = file.draftFsi.name
          } else if (file.targetFsi) {
            docName = file.targetFsi.name
          }
        }

        finalMsg = `Read and acknowledgement for document: ${docName}`
      }

      // Check if this is a revert action
      if (
        [
          WorkflowStepType.REVISE_EDIT,
          WorkflowStepType.REVISE_EDIT_MULTIFILE,
          WorkflowStepType.REVISE_EDIT_MIGRATION,
          WorkflowStepType.APPROVE_FILE_ARTIFACT,
          WorkflowStepType.REVISE_EDIT_FILE_ARTIFACT,
          WorkflowStepType.REVISE_EDIT_EOS,
          WorkflowStepType.REVIEW_FILE_ARTIFACT,
          WorkflowStepType.REVIEW,
          WorkflowStepType.REVIEW_MULTIFILE,
          WorkflowStepType.REVISE_EDIT_RECLASSIFICATION,
          WorkflowStepType.REVIEW_RECLASSIFICATION,
          WorkflowStepType.REVIEW_EOS,
          WorkflowStepType.APPROVE_RECLASSIFICATION,
          WorkflowStepType.APPROVE_MIGRATION,
          WorkflowStepType.APPROVE_MULTIFILE,
          WorkflowStepType.APPROVE,
          WorkflowStepType.APPROVE_EOS
          // @ts-expect-error - Complex workflow step type checking
        ].includes(params.workflowStepType) &&
        params.taskType === TaskType.USER_REJECTED_TASK
      ) {
        finalMsg = 'Task revert action'
        isRevert = true
      }

      if (!finalMsg) {
        finalMsg = 'Task approval action'
      }

      const checkEsignAndApprove = () => {
        const approveTaskInternal = async (esignature?: ESignature) => {
          try {
            const shouldIncludeComment = !params.isSimplified || (params.isSimplified && params.action === 2)

            const resp = await tasksApi.approveTask(taskId, shouldIncludeComment ? comment : undefined, esignature)

            if (onApprove && typeof onApprove === 'function') {
              onApprove()
            }

            const successMessage = params.successMsg || 'Task approved successfully'
            toast.success(successMessage)

            // Check for external editor upload
            if (!isRevert && (resp as unknown as Record<string, unknown>)?.uploadedFromExternalEditor) {
              toast.success('Document uploaded from external editor')
            }

            // Emit events for updates
            if (task.workflowStepType === WorkflowStepType.TRAINING) {
              window.dispatchEvent(new CustomEvent('readAcknowledgeTaskApproved'))
            }

            if (task.workflowStepType === WorkflowStepType.APPROVE) {
              // Adding a delay to get updated task details with targetFsi in Read & Acknowledge phase
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('updateTables'))
                window.dispatchEvent(new CustomEvent('updateTasks'))
              }, 1500)
            } else {
              window.dispatchEvent(new CustomEvent('updateTables'))
              window.dispatchEvent(new CustomEvent('updateTasks'))
            }

            resolve()
          } catch (error: unknown) {
            const err = error as Record<string, unknown>
            const errorMessage = ((err?.response as Record<string, unknown>)?.data as { message?: string } | undefined)
              ? ((err?.response as Record<string, unknown>)?.data as { message: string }).message
              : (err?.message as string) || 'Unknown error'
            toast.error(`Failed to approve task: ${errorMessage}`)
            reject(error)
          }
        }

        // Check if e-signature is required for this action
        if ((!isRevert && task.flags.approveESignatureRequired) || (isRevert && task.flags.revertESignatureRequired)) {
          if (signAction) {
            // Use the signAction callback (from useEsignature hook) to obtain validation
            signAction('ADMIN_ACTION', finalMsg)
              .then((validation) => {
                const esignature = normalizeValidationToESignature(validation)
                approveTaskInternal(esignature)
              })
              .catch(() => {
                // User cancelled e-signature
                toast.error('E-signature cancelled')
                reject(new Error('E-signature cancelled by user'))
              })
          } else if (params.esignature) {
            // Caller pre-provided an esignature object
            approveTaskInternal(params.esignature)
          } else {
            // No signAction and no pre-provided esignature — cannot proceed
            toast.error('E-signature is required but no authentication method was provided')
            reject(new Error('E-signature required but signAction not provided'))
          }
        } else {
          // No e-signature required
          approveTaskInternal()
        }
      }

      // Check OneDrive access if required
      if (task.flags.canUploadFromOneDrive && task.workflowUuid) {
        tasksApi
          .onedriveWfHasAccess(parseInt(task.workflowUuid))
          .then((hasAccess: boolean) => {
            if (!hasAccess) {
              // In a real React app, this would show a confirmation dialog
              // For now, we'll just proceed with approval
              checkEsignAndApprove()
            } else {
              checkEsignAndApprove()
            }
          })
          .catch(() => {
            checkEsignAndApprove()
          })
      } else {
        checkEsignAndApprove()
      }
    })
  }
}

export default TaskService
